import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { briefNote, briefNotes } from "../../../api/endpoints";
import { apiErrorCode } from "../../../utils/apiError";
import eventEmitter from "../../../utils/eventEmitter";
import { BriefNoteParam } from "../../../hooks/useSocket";
import { IBriefNote } from "../constants";

export const BRIEF_NOTES_LOCKED_MESSAGE = "Notes are read-only once the brief is approved.";
// Mirrors the API's BRIEF_NOTE_MAX_LENGTH (briefNotesController).
export const BRIEF_NOTE_MAX_LENGTH = 5000;

// api/index.ts already handles these: 401 logs out, 403 shows the global
// permission toast. Toasting again would stack a second message.
const isHandledGlobally = (err: any) => [401, 403].includes(err?.response?.status);

const writeErrorMessage = (err: unknown, fallback: string) => {
    switch (apiErrorCode(err)) {
        case "BriefNoteNotFound":
            return "This note no longer exists.";
        case "BriefNoteTooLong":
            return `Notes can be at most ${BRIEF_NOTE_MAX_LENGTH} characters.`;
        case "BriefNotFound":
        case "ProjectNotFound":
            return "This brief no longer exists.";
        default:
            return fallback;
    }
};

const sameId = (a: unknown, b: unknown) => a != null && b != null && String(a) === String(b);

// A write's response row, or nothing to merge if the body is not one.
const asRow = (data: unknown): Partial<IBriefNote> =>
    data && typeof data === "object" ? (data as Partial<IBriefNote>) : {};

/**
 * The notes thread of one brief (copied from the video-review comments'
 * useVideoFeedback, as a plain hook). Fetches on mount with a real loading
 * state, refetches quietly on the 'briefNote' socket event for this brief and
 * after each successful write, and
 * toasts failed writes itself — callers only learn whether a write succeeded,
 * so the composer can keep its draft on failure.
 */
export const useBriefNotes = (projectId?: number, briefId?: number) => {
    const enabled = !!projectId && !!briefId;
    const [notes, setNotes] = useState<IBriefNote[]>([]);
    const [loading, setLoading] = useState(enabled);
    const [saving, setSaving] = useState(false);

    // Monotonic read generation (as in useActivityFeed): only the latest read
    // may commit, so an out-of-order socket refetch, or a read that started
    // before a write, never overwrites newer rows.
    const genRef = useRef(0);
    // Reads still in flight. A superseded read clears `loading` only when no
    // read is left to land, so a socket refetch that overtakes the first load
    // keeps the spinner up until its rows arrive (in either order).
    const inFlightRef = useRef(0);
    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const fetchNotes = useCallback(
        async (silent = false) => {
            if (!projectId || !briefId) return;
            const myGen = ++genRef.current;
            inFlightRef.current++;
            if (!silent) setLoading(true);
            try {
                const { data } = await axiosInstant.get(briefNotes(projectId, briefId));
                if (!isMounted.current || myGen !== genRef.current) return;
                setNotes(Array.isArray(data) ? data : []);
            } catch (err) {
                if (!isMounted.current || myGen !== genRef.current) return;
                if (!silent && !isHandledGlobally(err)) toast.error("Failed to load notes.");
            } finally {
                inFlightRef.current--;
                if (isMounted.current && (myGen === genRef.current || inFlightRef.current === 0)) setLoading(false);
            }
        },
        [projectId, briefId]
    );

    useEffect(() => {
        setNotes([]);
        if (!projectId || !briefId) {
            setLoading(false);
            return;
        }
        fetchNotes();
    }, [projectId, briefId, fetchNotes]);

    useEffect(() => {
        if (!briefId) return undefined;
        const handleBriefNote = (param?: BriefNoteParam) => {
            if (sameId(param?.briefId, briefId)) fetchNotes(true);
        };
        eventEmitter.on("briefNote", handleBriefNote);
        return () => {
            eventEmitter.off("briefNote", handleBriefNote);
        };
    }, [briefId, fetchNotes]);

    const handleWriteError = useCallback(
        (err: unknown, fallback: string) => {
            if (!isMounted.current || isHandledGlobally(err)) return;
            // Someone else's delete (or a stale row): resync the thread.
            if (apiErrorCode(err) === "BriefNoteNotFound") fetchNotes(true);
            toast.error(writeErrorMessage(err, fallback));
        },
        [fetchNotes]
    );

    const createNote = useCallback(
        async (message: string): Promise<boolean> => {
            if (!projectId || !briefId) return false;
            // Drop any read that started before this write; the quiet resync
            // after a successful write replaces it, so a dropped read (e.g.
            // someone else's new note) is never lost if the socket is down.
            genRef.current++;
            setSaving(true);
            try {
                const { data } = await axiosInstant.post(briefNotes(projectId, briefId), { message });
                if (!isMounted.current) return true;
                fetchNotes(true);
                const row = asRow(data);
                // No row to show (unexpected body): the resync brings it.
                if (row.id == null) return true;
                // The socket echo's refetch can land before the POST resolves
                // and already hold the new row, so never append an id twice.
                // created_at fallback: the day-grouped list skips rows without one.
                const created = { ...row, created_at: row.created_at || new Date().toISOString() } as IBriefNote;
                setNotes((prev) => (prev.some((n) => sameId(n.id, created.id)) ? prev : [...prev, created]));
                return true;
            } catch (err) {
                handleWriteError(err, "Failed to add note. Please try again.");
                return false;
            } finally {
                if (isMounted.current) setSaving(false);
            }
        },
        [projectId, briefId, fetchNotes, handleWriteError]
    );

    const updateNote = useCallback(
        async (noteId: number, message: string): Promise<boolean> => {
            if (!projectId || !briefId) return false;
            genRef.current++;
            try {
                const { data } = await axiosInstant.patch(briefNote(projectId, briefId, noteId), { message });
                if (!isMounted.current) return true;
                fetchNotes(true);
                const row = asRow(data);
                setNotes((prev) =>
                    prev.map((n) =>
                        sameId(n.id, noteId) ? { ...n, message, edited: true, ...row, user: row.user ?? n.user } : n
                    )
                );
                return true;
            } catch (err) {
                handleWriteError(err, "Failed to update note.");
                return false;
            }
        },
        [projectId, briefId, fetchNotes, handleWriteError]
    );

    const deleteNote = useCallback(
        async (noteId: number): Promise<boolean> => {
            if (!projectId || !briefId) return false;
            genRef.current++;
            try {
                const { data } = await axiosInstant.delete(briefNote(projectId, briefId, noteId));
                if (!isMounted.current) return true;
                fetchNotes(true);
                const row = asRow(data);
                // Soft delete: the row stays in the thread, tagged "Deleted".
                setNotes((prev) =>
                    prev.map((n) =>
                        sameId(n.id, noteId) ? { ...n, ...row, deleted: true, user: row.user ?? n.user } : n
                    )
                );
                return true;
            } catch (err) {
                handleWriteError(err, "Failed to delete note.");
                return false;
            }
        },
        [projectId, briefId, fetchNotes, handleWriteError]
    );

    return { notes, loading, saving, createNote, updateNote, deleteNote };
};
